"use client";

import { useEffect, useRef } from "react";

type Star = {
  x: number;
  y: number;
  radius: number;
  phase: number;
  speed: number;
};

type Ring = { bornAt: number };

const RING_LIFESPAN = 5200;
const RING_INTERVAL = 4400;

export function HowlBackdrop() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }

    let width = 0;
    let height = 0;
    let stars: Star[] = [];
    let rings: Ring[] = [];
    let lastRingAt = -RING_INTERVAL;
    let animationFrame = 0;
    const startedAt = performance.now();

    const resize = () => {
      const bounds = canvas.getBoundingClientRect();
      width = bounds.width;
      height = bounds.height;
      const devicePixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = width * devicePixelRatio;
      canvas.height = height * devicePixelRatio;
      context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);

      const sceneHeight = Math.min(height, window.innerHeight);
      const count = Math.max(18, Math.min(96, Math.round((width * sceneHeight) / 4500)));
      stars = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * sceneHeight,
        radius: Math.random() * 1.1 + 0.3,
        phase: Math.random() * Math.PI * 2,
        speed: Math.random() * 0.5 + 0.25,
      }));
    };

    const animate = (now: number) => {
      const elapsed = now - startedAt;
      const sceneHeight = Math.min(height, window.innerHeight);
      context.clearRect(0, 0, width, height);

      for (const star of stars) {
        const twinkle = 0.35 + 0.65 * (0.5 + 0.5 * Math.sin((elapsed / 1000) * star.speed + star.phase));
        context.beginPath();
        context.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
        context.fillStyle = `rgb(236 231 223 / ${0.55 * twinkle})`;
        context.fill();
      }

      if (elapsed - lastRingAt >= RING_INTERVAL) {
        lastRingAt = elapsed;
        rings.push({ bornAt: elapsed });
      }
      rings = rings.filter((ring) => elapsed - ring.bornAt < RING_LIFESPAN);

      const originX = width * 0.5;
      const originY = Math.min(138, sceneHeight * 0.3);
      const maxRadius = Math.max(width, sceneHeight) * 0.9;
      for (const ring of rings) {
        const age = (elapsed - ring.bornAt) / RING_LIFESPAN;
        context.beginPath();
        context.arc(originX, originY, age * maxRadius, 0, Math.PI * 2);
        context.strokeStyle = `rgb(74 126 194 / ${(1 - age) * 0.22})`;
        context.lineWidth = 1.4;
        context.stroke();
      }

      animationFrame = requestAnimationFrame(animate);
    };

    resize();
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);
    animationFrame = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
    };
  }, []);

  return <canvas ref={canvasRef} aria-hidden className="pointer-events-none absolute inset-0 z-0 size-full" />;
}
